// =====================================================================
//  ELECTRICAL TABLES & HELPERS
// ---------------------------------------------------------------------
//  All values from SANS 10142-1 / IEC 60364-5-52, single-core PVC-
//  insulated copper conductors at 70 °C insulation, ambient 30 °C.
//  · Ampacity table assumes Method B (in conduit on a wall / surface).
//  · Voltage-drop figures are for 50 Hz AC at PF = 0.85.
//
//  Bear in mind: this is a planning-grade calculator. The actual
//  installation must always be certified by a registered installation
//  electrician issuing a CoC under the OHS Act.
// =====================================================================

export const CABLE_SIZES = [1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240, 300];

// Current carrying capacity (A) — Method B, copper PVC, 30 °C ambient.
//   2C  = single-phase (2-core run)
//   3C  = three-phase (3 / 4-core run)
export const AMPACITY = {
  1.5:  { c2: 17.5, c3: 15.5 },
  2.5:  { c2: 24,   c3: 21 },
  4:    { c2: 32,   c3: 28 },
  6:    { c2: 41,   c3: 36 },
  10:   { c2: 57,   c3: 50 },
  16:   { c2: 76,   c3: 68 },
  25:   { c2: 101,  c3: 89 },
  35:   { c2: 125,  c3: 110 },
  50:   { c2: 151,  c3: 134 },
  70:   { c2: 192,  c3: 171 },
  95:   { c2: 232,  c3: 207 },
  120:  { c2: 269,  c3: 239 },
  150:  { c2: 309,  c3: 275 },
  185:  { c2: 353,  c3: 314 },
  240:  { c2: 415,  c3: 370 },
  300:  { c2: 477,  c3: 426 },
};

// Voltage drop coefficient (mV per amp per metre).
//   Vd (V) = (mVAm × I × L) / 1000
export const VDROP = {
  1.5:  { sp: 29,   tp: 25 },
  2.5:  { sp: 18,   tp: 15 },
  4:    { sp: 11,   tp: 9.5 },
  6:    { sp: 7.3,  tp: 6.4 },
  10:   { sp: 4.4,  tp: 3.8 },
  16:   { sp: 2.8,  tp: 2.4 },
  25:   { sp: 1.75, tp: 1.5 },
  35:   { sp: 1.25, tp: 1.1 },
  50:   { sp: 0.93, tp: 0.80 },
  70:   { sp: 0.63, tp: 0.55 },
  95:   { sp: 0.47, tp: 0.41 },
  120:  { sp: 0.38, tp: 0.33 },
  150:  { sp: 0.30, tp: 0.26 },
  185:  { sp: 0.25, tp: 0.21 },
  240:  { sp: 0.19, tp: 0.165 },
  300:  { sp: 0.155,tp: 0.135 },
};

// Installation method derating (multiplier on ampacity)
export const INSTALL_METHODS = [
  { id: "conduit-wall", label: "In conduit on a wall (Method B)", factor: 1.00 },
  { id: "tray",         label: "Clipped to / on a cable tray (Method E)", factor: 1.15 },
  { id: "buried",       label: "Direct buried (Method D)",        factor: 1.08 },
  { id: "conduit-buried", label: "In conduit buried (Method D)",  factor: 0.95 },
  { id: "free-air",     label: "Free air (Method F)",             factor: 1.25 },
];

// Ambient temperature derating (PVC 70 °C insulation).
export const AMBIENT_FACTORS = [
  { t: 25, k: 1.06 },
  { t: 30, k: 1.00 },
  { t: 35, k: 0.94 },
  { t: 40, k: 0.87 },
  { t: 45, k: 0.79 },
  { t: 50, k: 0.71 },
  { t: 55, k: 0.61 },
];
export function ambientFactor(tempC) {
  // Linear interpolation between the listed temperatures.
  const list = AMBIENT_FACTORS;
  if (tempC <= list[0].t) return list[0].k;
  if (tempC >= list[list.length - 1].t) return list[list.length - 1].k;
  for (let i = 0; i < list.length - 1; i++) {
    const a = list[i], b = list[i + 1];
    if (tempC >= a.t && tempC <= b.t) {
      const t = (tempC - a.t) / (b.t - a.t);
      return a.k + (b.k - a.k) * t;
    }
  }
  return 1;
}

// Standard MCB / breaker ratings (A)
export const BREAKER_SIZES = [
  6, 10, 16, 20, 25, 32, 40, 50, 63, 80, 100, 125, 160, 200, 250, 300, 400, 500, 630,
];

/**
 *  Pick the cable size that satisfies both ampacity AND voltage drop.
 *  @param current_A    design current after diversity & margin
 *  @param length_m     run length
 *  @param threePhase   bool
 *  @param voltage      system voltage (230 / 400 / custom)
 *  @param vdMaxPct     allowable voltage drop, %
 *  @param installFactor ampacity derating multiplier
 *  @param ambientFact   ambient-temperature factor
 *  @returns {{ size, ampacity, vDrop_V, vDrop_pct, byAmpacity, byVdrop }}
 */
export function selectCableSize({
  current_A,
  length_m,
  threePhase,
  voltage,
  vdMaxPct,
  installFactor = 1,
  ambientFact = 1,
}) {
  const vdMaxV = (vdMaxPct / 100) * voltage;
  let byAmpacity = null;
  let byVdrop = null;
  let winner = null;

  for (const size of CABLE_SIZES) {
    const baseAmp = threePhase ? AMPACITY[size].c3 : AMPACITY[size].c2;
    const ampacity = baseAmp * installFactor * ambientFact;
    const vCoef = threePhase ? VDROP[size].tp : VDROP[size].sp;
    const vDrop_V = (vCoef * current_A * length_m) / 1000;

    const ampacityOK = ampacity >= current_A;
    const vdropOK    = vDrop_V <= vdMaxV;

    if (byAmpacity === null && ampacityOK) byAmpacity = size;
    if (byVdrop === null && vdropOK) byVdrop = size;

    if (ampacityOK && vdropOK && winner === null) {
      winner = { size, ampacity, vDrop_V, vDrop_pct: (vDrop_V / voltage) * 100 };
    }
  }
  return { ...winner, byAmpacity, byVdrop };
}

/** Pick the next breaker size up from the given current (or null if too big). */
export function selectBreaker(current_A) {
  return BREAKER_SIZES.find((b) => b >= current_A) ?? null;
}

/** Phase recommendation based on connected load (rule-of-thumb). */
export function recommendPhase(peakKw) {
  if (peakKw <= 7.5)  return { phase: "single", note: "Single phase 230 V is adequate." };
  if (peakKw <= 15)   return { phase: "either", note: "Single phase 230 V workable, three phase 400 V recommended for headroom and starting current." };
  return { phase: "three", note: "Three phase 400 V required — single-phase mains will not deliver this load." };
}
